/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React from 'react';
import { Globe, Linkedin, ExternalLink } from 'lucide-react';

/**
 * Sanitize an external URL for safe rendering: only http(s) is allowed,
 * everything else (javascript:, data:, relative) is dropped.
 */
export const safeExternalUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const v = raw.trim();
  if (!v) return null;
  try {
    const u = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.href;
  } catch {
    return null;
  }
};

const hostOf = (url) => {
  try { return new URL(url).host.replace(/^www\./, ''); } catch { return url; }
};

/**
 * Compact list of verified external links (website, LinkedIn) for a profile.
 * Links open in a new tab with rel="noopener noreferrer nofollow".
 */
export const ExternalLinks = ({ websiteUrl, linkedinUrl, className = '' }) => {
  const site = safeExternalUrl(websiteUrl);
  const li = safeExternalUrl(linkedinUrl);
  if (!site && !li) return null;
  const linkCls = 'inline-flex items-center gap-1.5 text-xs rounded-full border border-border px-2.5 py-1 text-foreground/80 hover:text-primary hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors';
  return (
    <div className={`flex flex-wrap gap-2 ${className}`} data-testid="profile-external-links">
      {site && (
        <a href={site} target="_blank" rel="noopener noreferrer nofollow" className={linkCls} data-testid="profile-website-link" aria-label={`Sito web: ${hostOf(site)}`}>
          <Globe className="h-3.5 w-3.5" /> {hostOf(site)} <ExternalLink className="h-3 w-3 opacity-60" />
        </a>
      )}
      {li && (
        <a href={li} target="_blank" rel="noopener noreferrer nofollow" className={linkCls} data-testid="profile-linkedin-link" aria-label="Profilo LinkedIn">
          <Linkedin className="h-3.5 w-3.5" /> LinkedIn <ExternalLink className="h-3 w-3 opacity-60" />
        </a>
      )}
    </div>
  );
};
